import { useReserveModal } from "@/hooks/useReserveModal";
import { api } from "@/lib/api/api";
import { OfficeSelect } from "@cmp-src/components/selects/OfficeSelect/OfficeSelect";
import { DayDate } from "@cmp-src/data-display/dates/DayDate";
import {
  AgendaSchedule,
  ISchedule,
  IWorkingLocation,
  IWorkingLocationPresenceType,
  WL_PRESENCE_OFFICE,
  WL_PRESENCE_OOO,
  WL_PRESENCE_REMOTE,
  parseDateOnlyString,
  Office,
  DivisionsTree
} from "@gettactic/api";
import { useRef, useEffect } from "react";
import {
  AgendaDay as AgendaDayType,
  OfficeAvailability
} from "@gettactic/api";
import { ReservationCard } from "@gettactic/components/src/components/reservation-card/ReservationCard";
import { capitalize, parseApiDate } from "@gettactic/helpers";
import { useDivisionsMenuQuery } from "@gettactic/hooks";
import {
  useAuthenticated,
  useWorkplaceRequestsAllowed
} from "@gettactic/hooks";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@v2/components/ui/tooltip";
import { cn } from "@v2/lib/utils";
import { useMapModalContext } from "@webV2/components/map-modal";
import { useReserve } from "@webV2/providers/ReserveProvider/context";
import { getDuplicateReservationData } from "@webV2/providers/ReserveProvider/getDuplicateReservationData";
import { format, isPast, isToday } from "date-fns";
import { Plus } from "lucide-react";
import { useRouter } from "next/router";
import { CheckInCardOnWeb } from "./CheckInCardOnWeb";

interface OptimisticLocation extends Partial<IWorkingLocation> {
  location: IWorkingLocationPresenceType;
  office_id?: string;
}

const findOfficeInTree = (
  tree: DivisionsTree | null | undefined,
  officeId: string | null | undefined
): Office | null => {
  if (!tree || !officeId) return null;

  const searchInNode = (node: DivisionsTree): Office | null => {
    // Check offices in current node
    if (node.offices) {
      const office = node.offices.find((o) => o.id === officeId);
      if (office) return office;
    }

    // Search in children
    if (node.children) {
      for (const child of node.children) {
        const found = searchInNode(child);
        if (found) return found;
      }
    }

    return null;
  };

  return searchInNode(tree);
};

export const AgendaDay = ({
  office_id,
  date,
  schedule,
  onSelectItemChange,
  selectedEvent,
  onLocationChange,
  workingLocationId,
  day,
  officeAvailability,
  location,
  isLoadingOfficeAvailability = false,
  isLocationLoading = false,
  optimisticLocation = null,
  isMobile = false,
  orgSlug
}: {
  office_id: string | null;
  date: string;
  schedule: AgendaSchedule[] | null;
  selectedEvent: AgendaSchedule | null;
  onSelectItemChange: (schedule: AgendaSchedule | null) => void;
  onLocationChange: (location: IWorkingLocationPresenceType) => void;
  workingLocationId: string | undefined;
  day: AgendaDayType | null;
  officeAvailability: OfficeAvailability | null;
  location: IWorkingLocationPresenceType | IWorkingLocation | null;
  isLoadingOfficeAvailability?: boolean;
  isLocationLoading?: boolean;
  optimisticLocation?: AgendaDayType | null;
  isMobile?: boolean;
  orgSlug?: string;
}) => {
  const router = useRouter();
  const { openModal, closeModal } = useMapModalContext();
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const isWorkplaceRequestsAllowed =
    useWorkplaceRequestsAllowed("workplace_requests");
  const divisions = useDivisionsMenuQuery();

  const { openDuplicateModal } = useReserve();
  const { openEditModal, openCancelModal, setReserveModalData } = useReserveModal();

  const selectedOfficeRef = useRef<Office | null>(null);

  const effectiveDay =
    optimisticLocation ||
    (day?.location === WL_PRESENCE_OFFICE ? office_id : day);

  const effectiveLocation =
    optimisticLocation?.location ||
    (typeof location === "string" ? location : location?.location);

  const effectiveWorkingLocationId =
    (optimisticLocation as OptimisticLocation)?.office_id || workingLocationId;

  useEffect(() => {
    const currentOfficeId =
      effectiveLocation === WL_PRESENCE_OFFICE
        ? office_id ?? effectiveWorkingLocationId ?? null
        : null;
    
    if (currentOfficeId && divisions.data?.tree) {
      const office = findOfficeInTree(divisions.data.tree, currentOfficeId);
      selectedOfficeRef.current = office;
    } else if (effectiveLocation !== WL_PRESENCE_OFFICE) {
      // Clear the ref when location changes to remote or OOO
      selectedOfficeRef.current = null;
    }
  }, [office_id, effectiveWorkingLocationId, effectiveLocation, divisions.data?.tree]);

  const handleOpenEditModal = (schedule: ISchedule) => {
    openEditModal(schedule, true, selectedOfficeRef.current ?? undefined);
  };

  const handleOpenCancelModal = (schedule: ISchedule) => {
    openCancelModal([schedule]);
  };

  // usually the same office for all the reservations
  // TBD: group schedule items by office later
  const defaultOffice = office_id;

  const isOOO = schedule?.some((item) => {
    // @ts-ignore - Expected error due to missing property
    return item.location === WL_PRESENCE_OOO;
  });

  // when ooo or remote assigned after the office is selected,
  // office is not being removed from the schedule
  // so we need to hide the office card
  const hideOfficeCard =
    effectiveLocation === WL_PRESENCE_REMOTE ||
    effectiveLocation === WL_PRESENCE_OOO;

  const fallbackOfficeSelectValue = defaultOffice ?? "";

  // Calculate the defaultValue for OfficeSelect
  let selectValue = "";
  // Handle all three types of locations:
  // 1. If it's remote or OOO, use the location value directly
  if (
    effectiveLocation === WL_PRESENCE_REMOTE ||
    effectiveLocation === WL_PRESENCE_OOO
  ) {
    selectValue = effectiveLocation;
  }
  // 2. If it's office, use the office ID
  else if (
    effectiveLocation === WL_PRESENCE_OFFICE &&
    (effectiveWorkingLocationId || office_id)
  ) {
    selectValue = office_id ?? effectiveWorkingLocationId ?? "";
  }
  // 3. Otherwise, use fallback
  else if (effectiveWorkingLocationId) {
    selectValue = effectiveWorkingLocationId;
  } else {
    selectValue = fallbackOfficeSelectValue;
  }
  const isPastDate = isPast(parseApiDate(date)) && !isToday(parseApiDate(date));

  const policy = day?.policy;

  const handleReservationClick = () => {
    // Use the selected office from OfficeSelect if available, otherwise use office_id from props
    const selectedOffice = selectedOfficeRef.current;
    const officeToUse = selectedOffice || (office_id ? authenticatedUser.offices?.byId[office_id] : null);
    if (!isMobile) {
      openModal({
        date: date,
        officeId: officeToUse?.id ?? office_id ?? undefined,
        resourceId: undefined,
        floorId: undefined,
        defaultTab: "desks"
      });
    } else {
      setReserveModalData({
        reserveModal: true,
        resourceType: "desk",
        start: parseApiDate(date),
        office: officeToUse ?? undefined
      });
    }
  };

  return (
    <>
      <div className={cn("flex flex-col", { "opacity-70": isLocationLoading })}>
        <div className="flex items-center justify-between space-x-2 px-1 text-sm">
          {!isMobile ? (
            <div className={cn("relative flex items-center gap-1")}>
              <DayDate apiDate={date} className="z-11" />
              {!isPastDate && (
                <button
                  className={cn("group flex items-center justify-center", {
                    "cursor-pointer ": !isPastDate
                  })}
                  aria-label="Open map reservation"
                  onClick={() => {
                    if (!isPastDate) {
                      openModal({
                        date: date,
                        officeId: selectedOfficeRef.current?.id ?? office_id ?? undefined,
                        reservationModalIsOpen: true,
                        onSuccess: () => {
                          closeModal();
                        }
                      });
                    }
                  }}
                >
                  <Plus className="h-3.5 w-3.5 text-muted-foreground transition-all group-hover:animate-elastic-spin group-hover:text-primary" />
                </button>
              )}
            </div>
          ) : (
            <div
              className={"cursor-pointer font-bold"}
              // Don't use this as a trigger on mobile
              // onClick={() => {
              //   const query: Record<string, string> = {};
              //   if (date) query.date = `${date}T12:00:00`;
              //   if (office_id) query.officeId = office_id;

              //   router.push({
              //     pathname: "/map",
              //     query
              //   });
              // }}
            >
              Working location
            </div>
          )}
          <div className="flex min-w-0 flex-1 items-center justify-end">
            <div className="flex items-center justify-end gap-1">
              {/* If policy is requested, don't allow user to change location */}
              {divisions.data && !policy ? (
                <OfficeSelect
                  scrollViewportClassname="px-0"
                  containerClassName="flex-1"
                  triggerClassName="md:truncate md:px-0 max-w-[175px] md:max-w-[258px] pr-0 md:pr-0"
                  showRemote={true}
                  showOOO={true}
                  defaultValue={selectValue}
                  divisions={divisions.data}
                  onValueChange={async (value) => {
                    // Update the selected office ref when office changes
                    if (value === WL_PRESENCE_OFFICE || (value && value !== WL_PRESENCE_REMOTE && value !== WL_PRESENCE_OOO)) {
                      const office = findOfficeInTree(divisions.data?.tree, value);
                      selectedOfficeRef.current = office;
                    } else {
                      selectedOfficeRef.current = null;
                    }
                    
                    await onLocationChange(
                      value as IWorkingLocationPresenceType
                    );
                  }}
                  disabled={isPastDate || !!policy}
                />
              ) : (
                <div className="h-3 w-1 bg-transparent" />
              )}
              {policy && (
                <PolicyButton
                  policy={policy}
                  status={effectiveLocation}
                  date={date}
                />
              )}
            </div>
          </div>
        </div>
        <div className={cn("flex flex-col gap-1")}>
          {!policy && schedule && schedule.length > 0 ? (
            schedule?.map((item) => {
              // const canEdit = getCurrentUserCanEdit(item, authenticatedUser);
              const isSelected = selectedEvent?.id === item.id;
              return (
                <CheckInCardOnWeb
                  key={item.id}
                  reservation={item}
                  onClick={() => {
                    if (isSelected) {
                      onSelectItemChange(null);
                      return;
                    }
                    onSelectItemChange(item);
                  }}
                  layoutProps={{
                    isSelected
                  }}
                  onEdit={async () => {
                    await handleOpenEditModal(item);
                  }}
                  onCancel={async () => {
                    await handleOpenCancelModal(item);
                  }}
                  onDuplicate={async () => {
                    if (!isMobile) {
                      const duplicateData = await getDuplicateReservationData({
                        item: item ?? null,
                        user: authenticatedUser,
                        isWorkplaceRequestsAllowed,
                        api,
                        shouldSelectResource: true,
                        authenticatedUser: authenticatedUser
                      });

                      openModal({
                        date: date,
                        officeId: selectedOfficeRef.current?.id ?? office_id ?? undefined,
                        reservationModalIsOpen: true,
                        duplicateData,
                        defaultTab: "desks",
                        onSuccess: () => {
                          closeModal();
                        }
                      });
                    } else {
                      setTimeout(() => {
                        openDuplicateModal(item).then();
                      }, 200);
                    }
                  }}
                />
              );
            })
          ) : (
            <ReservationCard
              isPastDate={isPastDate}
              isInOffice={
                !hideOfficeCard &&
                effectiveDay &&
                typeof effectiveDay === "object" &&
                "office" in effectiveDay
                  ? !!effectiveDay.office
                  : day?.location === WL_PRESENCE_OFFICE
              }
              onReservationClick={handleReservationClick}
              disabled={isPastDate || !!policy}
              isLoadingDesksAvailability={isLoadingOfficeAvailability}
              availableDesks={officeAvailability?.available_desks}
              totalDesks={officeAvailability?.total_desks}
            />
          )}
        </div>
      </div>
    </>
  );
};

const PolicyButton = ({
  policy,
  status,
  date
}: {
  policy: AgendaDayType["policy"];
  date: string;
  status: IWorkingLocationPresenceType | undefined;
}) => {
  const statusName =
    status === WL_PRESENCE_REMOTE
      ? "Remote"
      : status === WL_PRESENCE_OOO
        ? "Out of Office"
        : null;
  const router = useRouter();
  if (!policy) {
    return null;
  }
  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            className="cursor-pointer py-3"
            onClick={() =>
              router.push({
                pathname: "/settings/attendance",
                query: {
                  start: format(parseDateOnlyString(date), "yyyy-MM-dd")
                }
              })
            }
          >
            {statusName} ({capitalize(policy.type)} Policy)
          </button>
        </TooltipTrigger>
        <TooltipContent>My Schedules Requests</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
